// service/chatApiSlice.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { ClientMetaPayload, PageContextPayload } from '../../helpers/pageContext'

export type ChatPayload = {
  prompt: string
  agentId: string
  chatId?: string // <-- add this: "new" or real id handled by hook
  pageContext?: PageContextPayload | null
  clientMeta?: ClientMetaPayload | null
}

export type ChatResponse = {
  chat: {
    id: string
    name: string
    updated_at: string
  }
  message: {
    content: string
    id: string
    model_slug: string
    role: string
    chat_id: string
    metadata?: Record<string, any> | null
    ragSources?: any[]
    rag_sources?: any[]
    kbSources?: any[]
    kb_sources?: any[]
    siteCrawlSources?: any[]
    site_crawl_sources?: any[]
  }
}

export type NarratePagePayload = {
  agentId: string
  url: string
  title?: string
  excerpt?: string
}

export type NarratePageResponse = {
  summary: string
  url: string
  title: string
}

export type PageNarrationCapabilityResponse = {
  pageNarration: {
    supported: boolean
    supportsTextSummary: boolean
    supportsSpeech: boolean
  }
}

export type SynthesizeSpeechPayload = {
  agentId: string
  prompt: string
  generateConfig?: Record<string, any>
}

export type SynthesizeSpeechResponse = {
  speeches: string[]
}

export const createChatApiSlice = (baseApiUrl: string) =>
  createApi({
    reducerPath: 'api/chat',
    baseQuery: fetchBaseQuery({
      baseUrl: baseApiUrl,
    }),
    endpoints: (builder) => ({
      getPageNarrationCapability: builder.query<PageNarrationCapabilityResponse, { agentId: string }>({
        query: ({ agentId }) => ({
          url: `/agents/${agentId}/page-narration`,
          headers: {
            'x-agent-id': agentId,
          },
        }),
        transformResponse: (response: {
          page_narration: {
            supported: boolean
            supportsTextSummary: boolean
            supportsSpeech: boolean
          }
        }) => ({
          pageNarration: response.page_narration,
        }),
      }),

      invokeChat: builder.mutation<ChatResponse, ChatPayload>({
        query: ({ agentId, prompt, chatId, pageContext, clientMeta }) => ({
          // ✅ "new" for first message, real id for others
          url: `/agents/${agentId}/chat/${chatId ?? 'new'}/messages`,
          method: 'POST',
          headers: {
            'x-agent-id': agentId,
          },
          body: {
            content: prompt,
            agent_id: agentId,
            ...(pageContext ? { page_context_json: JSON.stringify(pageContext) } : {}),
            ...(clientMeta ? { client_meta_json: JSON.stringify(clientMeta) } : {}),
          },
        }),
      }),

      // Step 1 of "read this page aloud": summarize the captured page
      // context into a short spoken-style narration (text only).
      narratePage: builder.mutation<NarratePageResponse, NarratePagePayload>({
        query: ({ agentId, url, title, excerpt }) => ({
          url: `/agents/${agentId}/page-narration`,
          method: 'POST',
          headers: {
            'x-agent-id': agentId,
          },
          body: {
            agent_id: agentId,
            url,
            title,
            excerpt,
          },
        }),
      }),

      // Step 2: synthesize the narration text into audio via the agent's
      // existing speech endpoint.
      synthesizeSpeech: builder.mutation<SynthesizeSpeechResponse, SynthesizeSpeechPayload>({
        query: ({ agentId, prompt, generateConfig }) => ({
          url: `/agents/${agentId}/speech`,
          method: 'POST',
          headers: {
            'x-agent-id': agentId,
          },
          body: {
            agent_id: agentId,
            prompt,
            ...(generateConfig ? { generate_config: JSON.stringify(generateConfig) } : {}),
          },
        }),
      }),
    }),
  })
